import { useState } from 'react';
import { X, Plus } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { DetailContent, LocationOption, Event } from '../types';

interface PostscriptEditorProps {
  postscript: (DetailContent | LocationOption)[];
  onSave: (postscript: (DetailContent | LocationOption)[]) => void;
  onClose: () => void;
  open: boolean;
}

export function PostscriptEditor({ 
  postscript,
  onSave,
  onClose,
  open
}: PostscriptEditorProps) {
  const [editingContent, setEditingContent] = useState<(DetailContent | LocationOption)[]>(postscript);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Edit Postscript Content</DialogTitle>
        </DialogHeader>
        
        <ScrollArea className="max-h-[70vh] pr-4">
          <div className="space-y-4">
            <div className="space-y-2">
              {editingContent.map((item, index) => (
                <div key={index} className="space-y-2 p-4 border rounded-lg">
                  <div className="flex justify-between items-center">
                    <Label>Content Type</Label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newContent = [...editingContent];
                        newContent.splice(index, 1);
                        setEditingContent(newContent);
                      }}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <select
                    value={item.type}
                    onChange={(e) => {
                      const type = e.target.value as Event["Chat"] | Event["Html"] | Event["Embed"] | Event["Map"] | Event["Location"];
                      let content: any = {};
                      
                      // Initialize appropriate content structure based on type
                      switch (type) {
                        case "CHAT":
                        case "HTML":
                          content = "";
                          break;
                        case "EMBED":
                          content = { url: "" };
                          break;
                        case "MAP":
                          content = { poiId: 0, state: null };
                          break;
                        case "LOCATION":
                          content = {
                            type: "LOCATION",
                            title: "",
                            subtitle: "",
                            description: "",
                            locationId: "",
                          };
                          break;
                      }
                      
                      const newContent = [...editingContent];
                      newContent[index] = { type, content };
                      setEditingContent(newContent);
                    }}
                    className="w-full p-2 border rounded-md"
                  >
                    <option value="CHAT">Chat</option>
                    <option value="HTML">HTML</option>
                    <option value="EMBED">Embed</option>
                    <option value="MAP">Map</option>
                    <option value="LOCATION">Location</option>
                  </select>
                  
                  {/* Content editor based on type */}
                  {item.type === "CHAT" || item.type === "HTML" ? (
                    <Textarea
                      value={(item as DetailContent).content}
                      onChange={(e) => {
                        const newContent = [...editingContent];
                        newContent[index] = { ...item, content: e.target.value };
                        setEditingContent(newContent);
                      }}
                      placeholder={`Enter ${item.type.toLowerCase()} content...`}
                      className="mt-2"
                    />
                  ) : item.type === "EMBED" ? (
                    <div className="space-y-2 mt-2">
                      <Label>URL</Label>
                      <Input
                        value={(item as DetailContent).content.url}
                        onChange={(e) => {
                          const newContent = [...editingContent];
                          newContent[index] = {
                            ...item,
                            content: { url: e.target.value }
                          };
                          setEditingContent(newContent);
                        }}
                        placeholder="Enter embed URL..."
                      />
                    </div>
                  ) : item.type === "MAP" ? (
                    <div className="space-y-2 mt-2">
                      <div className="space-y-2">
                        <Label>POI ID</Label>
                        <Input
                          type="number"
                          value={(item as DetailContent).content.poiId}
                          onChange={(e) => {
                            const newContent = [...editingContent];
                            newContent[index] = {
                              ...item,
                              content: {
                                ...(item as DetailContent).content,
                                poiId: parseInt(e.target.value) || 0
                              }
                            };
                            setEditingContent(newContent);
                          }}
                          placeholder="Enter POI ID..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>State</Label>
                        <Input
                          value={(item as DetailContent).content.state || ''}
                          onChange={(e) => {
                            const newContent = [...editingContent];
                            newContent[index] = {
                              ...item,
                              content: {
                                ...(item as DetailContent).content,
                                state: e.target.value || null
                              }
                            };
                            setEditingContent(newContent);
                          }}
                          placeholder="Enter map state..."
                        />
                      </div>
                    </div>
                  ) : item.type === "LOCATION" ? (
                    <div className="space-y-2 mt-2">
                      <div className="space-y-2">
                        <Label>Location ID</Label>
                        <Input
                          value={(item as LocationOption).locationId}
                          onChange={(e) => {
                            const newContent = [...editingContent];
                            newContent[index] = {
                              ...item,
                              locationId: e.target.value
                            } as LocationOption;
                            setEditingContent(newContent);
                          }}
                          placeholder="Enter location ID..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Title</Label>
                        <Input
                          value={(item as LocationOption).title}
                          onChange={(e) => {
                            const newContent = [...editingContent];
                            newContent[index] = {
                              ...item,
                              title: e.target.value
                            } as LocationOption;
                            setEditingContent(newContent);
                          }}
                          placeholder="Enter location title..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Subtitle</Label>
                        <Input
                          value={(item as LocationOption).subtitle}
                          onChange={(e) => {
                            const newContent = [...editingContent];
                            newContent[index] = {
                              ...item,
                              subtitle: e.target.value
                            } as LocationOption;
                            setEditingContent(newContent);
                          }}
                          placeholder="Enter location subtitle..."
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Description</Label>
                        <Textarea
                          value={(item as LocationOption).description}
                          onChange={(e) => {
                            const newContent = [...editingContent];
                            newContent[index] = {
                              ...item,
                              description: e.target.value
                            } as LocationOption;
                            setEditingContent(newContent);
                          }}
                          placeholder="Enter location description..."
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setEditingContent([
                  ...editingContent,
                  { type: "CHAT", content: "" }
                ]);
              }}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Content Item
            </Button>
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              onSave(editingContent);
              onClose();
            }}
          >
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
